import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { cn } from '@/lib/utils';
import {
  ThumbsUp,
  ThumbsDown,
  Flag,
  Eye,
  Share,
  Grid3X3,
  Image as ImageIcon,
  Calendar,
  Crop,
  RotateCcw,
  Type,
  Loader2
} from 'lucide-react';
import { ExternalLink } from 'lucide-react';
import { ApprovalActionDialog } from '../dialogs/ApprovalActionDialog';
import { useToast } from '@/hooks/use-toast';
import { ApprovalStatusBadge } from '../history/ApprovalAttribution';
import type { ApprovalItem, FranchiseRegion } from '../../types';
import { buildJobberJobUrl } from '@/features/jobber';
import { useTextDetection } from '@/features/gallery/hooks/useTextDetection';
import { useImageCrop } from '@/features/gallery/hooks/useImageCrop';
import { isCloudflareImageUrl } from '@/lib/cloudflareImages';

interface ApprovalSwipeCardProps {
  item: ApprovalItem;
  onApprove: (item: ApprovalItem, reason?: string) => Promise<boolean>;
  onReject: (item: ApprovalItem, reason?: string) => Promise<boolean>;
  onFlag: (item: ApprovalItem, reason?: string) => Promise<boolean>;
  onView?: (item: ApprovalItem) => void;
  onShare?: (item: ApprovalItem) => void;
  className?: string;
  showApprovalHistory?: boolean;
}

interface DragState {
  isDragging: boolean;
  dragDelta: number;
  startX: number;
}

const franchiseRegions: { value: FranchiseRegion; label: string }[] = [
  { value: 'HQ', label: 'Headquarters' },
  { value: 'toronto', label: 'Toronto' },
  { value: 'hamilton', label: 'Hamilton' },
  { value: 'mississauga', label: 'Mississauga' },
  { value: 'brampton', label: 'Brampton' },
  { value: 'north_york', label: 'North York' },
  { value: 'scarborough', label: 'Scarborough' },
  { value: 'london', label: 'London' },
  { value: 'general', label: 'General' }
];

const ApprovalSwipeCard: React.FC<ApprovalSwipeCardProps> = ({
  item,
  onApprove,
  onReject,
  onFlag,
  onView,
  onShare,
  className,
  showApprovalHistory = false
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragDelta: 0,
    startX: 0
  });
  const [actionState, setActionState] = useState<'none' | 'approve' | 'reject' | 'flag'>('none');
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | 'flag' | null>(null);

  // Text detection state
  const [hasDetectedText, setHasDetectedText] = useState<boolean | null>(null);
  const { detectText, isDetecting } = useTextDetection({ scanBottomOnly: true });

  // Crop state with persistence - only for images with valid Cloudflare URLs
  const canCrop = item.type === 'image' && item.image_url && isCloudflareImageUrl(item.image_url);
  const { isCropped, toggleCrop, isUpdating: isCropUpdating } = useImageCrop({
    imageId: item.id,
    imageUrl: item.image_url || '',
  });

  const metadata = (item.metadata ?? {}) as { jobber_web_uri?: string | null };
  const jobberLink = buildJobberJobUrl({
    jobberWebUri: metadata.jobber_web_uri,
    jobNumber: item.job_number
  });

  // Use the image URL directly - it's already the correct variant in the database
  const currentImageUrl = item.image_url;

  // Auto-detect text when image loads (only for Cloudflare images)
  useEffect(() => {
    if (item.image_url && item.type === 'image' && isCloudflareImageUrl(item.image_url) && hasDetectedText === null) {
      // Run text detection in background
      detectText(item.image_url).then(result => {
        setHasDetectedText(result.hasText);
      }).catch(() => {
        setHasDetectedText(false);
      });
    }
  }, [item.image_url, item.type, detectText, hasDetectedText]);

  // Reset text detection state when item changes
  useEffect(() => {
    setHasDetectedText(null);
  }, [item.id]);

  const handleToggleCrop = () => {
    // toggleCrop from useImageCrop saves the change to Supabase
    toggleCrop();
  };

  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragState({
      isDragging: true,
      dragDelta: 0,
      startX: clientX
    });
  };

  const handleDragMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!dragState.isDragging) return;
    
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragState.startX;
    
    setDragState(prev => ({
      ...prev,
      dragDelta: deltaX
    }));
  };

  const handleDragEnd = () => {
    if (!dragState.isDragging) return;

    const threshold = 100;
    const { dragDelta } = dragState;

    if (Math.abs(dragDelta) > threshold) {
      if (dragDelta > 0) {
        // Swipe right - approve (no dialog)
        setActionState('approve');
        setTimeout(async () => {
          const ok = await onApprove(item);
          setActionState('none');
          if (ok) {
            toast({
              title: 'Success',
              description: `${item.type} approved successfully`,
              variant: 'default'
            });
          } else {
            toast({
              title: 'Error',
              description: `Failed to approve ${item.type}`,
              variant: 'destructive'
            });
          }
        }, 250);
      } else {
        // Swipe left - reject
        setActionState('reject');
        setTimeout(() => {
          setPendingAction('reject');
          setShowActionDialog(true);
          setActionState('none');
        }, 300);
      }
    }

    setDragState({
      isDragging: false,
      dragDelta: 0,
      startX: 0
    });
  };

  const handleActionClick = (action: 'approve' | 'reject' | 'flag') => {
    if (action === 'approve') {
      // Instant approve without dialog
      void (async () => {
        const ok = await onApprove(item);
        if (ok) {
          toast({
            title: 'Success',
            description: `${item.type} approved successfully`,
            variant: 'default'
          });
        } else {
          toast({
            title: 'Error',
            description: `Failed to approve ${item.type}`,
            variant: 'destructive'
          });
        }
      })();
      return;
    }
    setPendingAction(action);
    setShowActionDialog(true);
  };

  const handleActionConfirm = (action: 'approve' | 'reject' | 'flag', reason?: string) => {
    switch (action) {
      case 'approve':
        void onApprove(item, reason);
        break;
      case 'reject':
        void onReject(item, reason);
        break;
      case 'flag':
        void onFlag(item, reason);
        break;
    }
    setShowActionDialog(false);
    setPendingAction(null);
  };

  const handleDialogClose = () => {
    setShowActionDialog(false);
    setPendingAction(null);
  };


  const getRegionBadge = (region: FranchiseRegion) => {
    const regionInfo = franchiseRegions.find(r => r.value === region);
    return <Badge variant="outline">{regionInfo?.label || region}</Badge>;
  };

  const getBgColor = () => {
    if (actionState === 'approve') return 'bg-success/10 dark:bg-success/20';
    if (actionState === 'reject') return 'bg-destructive/10 dark:bg-destructive/20';
    if (actionState === 'flag') return 'bg-warning/10 dark:bg-warning/20';
    return '';
  };

  const getTransformStyle = () => {
    if (dragState.isDragging) {
      return {
        transform: `translateX(${dragState.dragDelta}px) rotate(${dragState.dragDelta * 0.05}deg)`,
        transition: 'none'
      };
    }
    
    if (actionState === 'approve') {
      return {
        transform: 'translateX(100%) rotate(5deg)',
        transition: 'transform 0.3s ease'
      };
    }
    
    if (actionState === 'reject') {
      return {
        transform: 'translateX(-100%) rotate(-5deg)',
        transition: 'transform 0.3s ease'
      };
    }
    
    return {
      transform: 'translateX(0) rotate(0)',
      transition: 'transform 0.3s ease'
    };
  };

  return (
    <div className={cn("relative", className)}>
      <Card 
        ref={cardRef}
        className={cn(
          "overflow-hidden cursor-grab active:cursor-grabbing hover:shadow-lg transition-shadow",
          getBgColor()
        )}
        style={getTransformStyle()}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        onMouseDown={handleDragStart}
        onMouseMove={dragState.isDragging ? handleDragMove : undefined}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
      >
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Image Section */}
            <div className="w-full sm:w-64 flex-shrink-0 relative">
              <AspectRatio ratio={16 / 10} className="bg-muted">
                {currentImageUrl ? (
                  <img
                    src={currentImageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className="fallback-icon hidden absolute inset-0 flex items-center justify-center">
                  {item.type === 'gallery' ? (
                    <Grid3X3 className="h-12 w-12 text-muted-foreground/50" />
                  ) : (
                    <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                  )}
                </div>

                {/* Text Detection Badge - Top Left */}
                {item.type === 'image' && (
                  <div className="absolute top-2 left-2 flex items-center gap-1">
                    {isDetecting && (
                      <Badge variant="secondary" className="text-xs bg-background/80 backdrop-blur-sm">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Scanning...
                      </Badge>
                    )}
                    {hasDetectedText === true && !isDetecting && (
                      <Badge variant="warning" className="text-xs bg-warning/90 text-warning-foreground">
                        <Type className="h-3 w-3 mr-1" />
                        Has Text
                      </Badge>
                    )}
                    {isCropped && (
                      <Badge variant="secondary" className="text-xs bg-info/90 text-info-foreground">
                        <Crop className="h-3 w-3 mr-1" />
                        Cropped
                      </Badge>
                    )}
                  </div>
                )}

                {/* Crop/Reset Button - Bottom Right */}
                {canCrop && (
                  <div className="absolute bottom-2 right-2">
                    <Button
                      size="sm"
                      variant={isCropped ? "secondary" : "outline"}
                      className={cn(
                        "h-8 text-xs bg-background/80 backdrop-blur-sm hover:bg-background/90",
                        isCropped && "bg-info/20 border-info"
                      )}
                      disabled={isCropUpdating}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCrop();
                      }}
                    >
                      {isCropUpdating ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Saving...
                        </>
                      ) : isCropped ? (
                        <>
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Reset
                        </>
                      ) : (
                        <>
                          <Crop className="h-3 w-3 mr-1" />
                          Crop
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </AspectRatio>
            </div>

            {/* Content Section */}
            <div className="flex-1 p-4 space-y-3">
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-lg line-clamp-2">{item.title}</h3>
                  <Badge variant={item.type === 'gallery' ? 'default' : 'secondary'} className="ml-2 flex-shrink-0">
                    {item.type === 'gallery' ? 'Gallery' : 'Image'}
                  </Badge>
                </div>
                
                {item.description && (
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {item.description}
                  </p>
                )}
              </div>

              {/* Metadata */}
              <div className="flex flex-wrap gap-2">
                {getRegionBadge(item.franchise_region)}
                {item.job_number && (
                  <Badge variant="outline" className="text-xs">
                    Job #{item.job_number}
                  </Badge>
                )}
                {item.service_name && (
                  <Badge variant="outline" className="text-xs bg-info/10 text-info">
                    {item.service_name}
                  </Badge>
                )}
                <ApprovalStatusBadge 
                  status={item.review_status}
                  attribution={item.metadata?.approval_attribution}
                  showApprover={showApprovalHistory}
                />
              </div>

              {/* Date and Jobber Link */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                {new Date(item.created_at).toLocaleDateString()}
                {item.type === 'image' && jobberLink && (
                  <a
                    href={jobberLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Jobber job in new tab"
                    className="ml-auto inline-flex items-center text-info hover:underline"
                  >
                    Jobber <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                )}
              </div>

              {/* Action Buttons - Desktop */}
              <div className="hidden sm:flex items-center justify-between pt-2 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onView?.(item)}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-success hover:bg-success/10"
                    onClick={() => handleActionClick('approve')}
                  >
                    <ThumbsUp className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => handleActionClick('reject')}
                  >
                    <ThumbsDown className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-warning hover:bg-warning/10"
                    onClick={() => handleActionClick('flag')}
                  >
                    <Flag className="w-4 h-4" />
                  </Button>
                  {onShare && item.type === 'gallery' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-info hover:bg-info/10"
                      onClick={() => onShare(item)}
                    >
                      <Share className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Swipe Overlays */}
      {dragState.isDragging && (
        <>
          {dragState.dragDelta < -50 && (
            <div className="absolute inset-0 bg-destructive/20 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <ThumbsDown className="h-12 w-12 text-destructive mx-auto mb-2" />
                <span className="text-destructive font-semibold">Reject</span>
              </div>
            </div>
          )}
          {dragState.dragDelta > 50 && (
            <div className="absolute inset-0 bg-success/20 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <ThumbsUp className="h-12 w-12 text-success mx-auto mb-2" />
                <span className="text-success font-semibold">Approve</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Mobile Action Buttons */}
      <div className="sm:hidden flex justify-center space-x-4 mt-4">
        <Button
          size="lg"
          variant="outline"
          className="text-destructive hover:bg-destructive/10 flex-1"
          onClick={() => handleActionClick('reject')}
        >
          <ThumbsDown className="w-5 h-5 mr-2" />
          Reject
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-warning hover:bg-warning/10"
          onClick={() => handleActionClick('flag')}
        >
          <Flag className="w-5 h-5" />
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="text-success hover:bg-success/10 flex-1"
          onClick={() => handleActionClick('approve')}
        >
          <ThumbsUp className="w-5 h-5 mr-2" />
          Approve
        </Button>
      </div>

      {/* Approval Action Dialog */}
      <ApprovalActionDialog
        isOpen={showActionDialog}
        onClose={handleDialogClose}
        onConfirm={handleActionConfirm}
        entityType={item.type}
        entityTitle={item.title}
        defaultAction={pendingAction || 'approve'}
      />
    </div>
  );
};

export default ApprovalSwipeCard;
